import { useMemo, useState } from 'react'
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, RefreshCw, X } from 'lucide-react'
import { Button, LoadFailure, formatDate, Skeleton } from '../../core/index.js'
import type { AuditAdminApi } from '../adminApi.js'
import type { ConfigChange, ConfigChangeArea, ConfigChangeFilter } from '../types.js'
import { ConfigChangeRow } from './ConfigChangeRow.js'
import { ConfigChangesSummary, type SummaryPeriod } from './ConfigChangesSummary.js'
import { AREA_LABEL, AREAS, actorName, localDay } from './configChangeText.js'

/** The admin calls this screen uses. */
type AdminApi = AuditAdminApi

/** A filter as the page holds it: what it sends, and what its chip says. */
interface Narrowing {
  area?: ConfigChangeArea
  client?: { id: string; label: string }
  subject?: { key: string; label: string }
  actor?: { key: string; label: string }
  from?: string
  to?: string
}

function toFilter(narrowing: Narrowing): ConfigChangeFilter {
  return {
    ...(narrowing.area ? { area: narrowing.area } : {}),
    ...(narrowing.client ? { clientId: narrowing.client.id } : {}),
    ...(narrowing.subject ? { subject: narrowing.subject.key } : {}),
    ...(narrowing.actor ? { actor: narrowing.actor.key } : {}),
    // The date inputs are local days; the API takes instants, so each runs from the start of its first day to the end of its last.
    ...(narrowing.from ? { from: new Date(`${narrowing.from}T00:00:00`).toISOString() } : {}),
    ...(narrowing.to ? { to: new Date(`${narrowing.to}T23:59:59.999`).toISOString() } : {}),
  }
}

function Chip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-marine/30 bg-white px-2.5 py-0.5 text-[11px] text-marine">
      {label}
      <button type="button" onClick={onClear} aria-label={`Clear ${label}`} className="text-marine hover:text-ink">
        <X className="h-3 w-3" aria-hidden="true" />
      </button>
    </span>
  )
}

/**
 * The root admin's page, beside the sign-in history: every change to a
 * setting or a login - facility settings and details, behaviors, add-ons,
 * holiday events, client registrations and every kind of login - with who
 * made it, when, and field by field what it was and became. It opens with
 * the window's counts; an area, a person, a client or a record narrows the
 * list to them, and a record's name shows its whole history.
 */
export function ConfigChangesPage({ api }: { api: AdminApi }) {
  const queryClient = useQueryClient()
  const [days, setDays] = useState<SummaryPeriod>(30)
  const [narrowing, setNarrowing] = useState<Narrowing>({})
  const filter = useMemo(() => toFilter(narrowing), [narrowing])
  const summaryQuery = useQuery({ queryKey: ['admin', 'config-changes', 'summary', days], queryFn: () => api.summarizeConfigChanges(days) })
  const listQuery = useInfiniteQuery({
    queryKey: ['admin', 'config-changes', 'list', filter],
    queryFn: ({ pageParam }) => api.listConfigChanges(filter, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor,
  })
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['admin', 'config-changes'] })
  const changes = useMemo(() => listQuery.data?.pages.flatMap((page) => page.items) ?? [], [listQuery.data])
  const byDay = useMemo(() => {
    const groups = new Map<string, ConfigChange[]>()
    for (const change of changes) {
      const day = localDay(change.at)
      groups.set(day, [...(groups.get(day) ?? []), change])
    }
    return [...groups.entries()]
  }, [changes])
  const narrowed = Object.keys(narrowing).length > 0
  const update = (changes: Partial<Narrowing>) => setNarrowing((current) => Object.fromEntries(Object.entries({ ...current, ...changes }).filter(([, value]) => value !== undefined && value !== '')) as Narrowing)

  return (
    <div className="max-w-5xl mx-auto px-7 py-6">
      <p className="max-w-2xl text-xs leading-relaxed text-ink-mid mb-4">
        Only the root admin sees this page. It records every change to a setting or a login - who made it, when, and each field before and after - from the
        moment it was deployed. Worker records, shifts, outcomes and schedules are not configuration and are not here. Passwords are never stored, only that one
        changed.
      </p>

      {summaryQuery.isPending ? (
        <div className="mb-6 text-sm text-ink-mid">Counting changes…</div>
      ) : summaryQuery.isError ? (
        <div className="mb-6">
          <LoadFailure message="Could not count the configuration changes. If the API was deployed before this page existed, deploy it to switch this on." onRetry={() => void summaryQuery.refetch()} retrying={summaryQuery.isFetching} />
        </div>
      ) : (
        <ConfigChangesSummary
          summary={summaryQuery.data}
          days={days}
          onDays={setDays}
          onArea={(area) => update({ area })}
          onActor={(key, label) => update({ actor: { key, label } })}
        />
      )}

      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">
            Area
            <select
              value={narrowing.area ?? ''}
              onChange={(event) => update({ area: (event.target.value || undefined) as ConfigChangeArea | undefined })}
              className="mt-1 block border border-marine/30 bg-white px-2 py-1.5 text-xs font-normal normal-case tracking-normal text-ink"
            >
              <option value="">Every area</option>
              {AREAS.map((area) => (
                <option key={area} value={area}>
                  {AREA_LABEL[area]}
                </option>
              ))}
            </select>
          </label>
          <label className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">
            From
            <input type="date" value={narrowing.from ?? ''} onChange={(event) => update({ from: event.target.value || undefined })} className="mt-1 block border border-marine/30 bg-white px-2 py-1 text-xs font-normal text-ink" />
          </label>
          <label className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">
            To
            <input type="date" value={narrowing.to ?? ''} onChange={(event) => update({ to: event.target.value || undefined })} className="mt-1 block border border-marine/30 bg-white px-2 py-1 text-xs font-normal text-ink" />
          </label>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={listQuery.isFetching}
          title={listQuery.isFetching ? 'Refreshing…' : 'Refresh'}
          aria-label={listQuery.isFetching ? 'Refreshing' : 'Refresh'}
          className="inline-flex items-center rounded border border-marine/30 bg-white p-2 text-marine hover:bg-marine-lt/40 disabled:opacity-60"
        >
          <RefreshCw aria-hidden="true" strokeWidth={2.25} className={`h-3.5 w-3.5 ${listQuery.isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {narrowed && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {narrowing.area && <Chip label={AREA_LABEL[narrowing.area]} onClear={() => update({ area: undefined })} />}
          {narrowing.client && <Chip label={`Client: ${narrowing.client.label}`} onClear={() => update({ client: undefined })} />}
          {narrowing.subject && <Chip label={`Record: ${narrowing.subject.label}`} onClear={() => update({ subject: undefined })} />}
          {narrowing.actor && <Chip label={`By ${narrowing.actor.label}`} onClear={() => update({ actor: undefined })} />}
          {narrowing.from && <Chip label={`From ${formatDate(`${narrowing.from}T00:00:00`)}`} onClear={() => update({ from: undefined })} />}
          {narrowing.to && <Chip label={`To ${formatDate(`${narrowing.to}T00:00:00`)}`} onClear={() => update({ to: undefined })} />}
          <button type="button" onClick={() => setNarrowing({})} className="text-[11px] text-marine underline underline-offset-2">
            Clear all
          </button>
        </div>
      )}

      {listQuery.isPending ? (
        <Skeleton lines={5} label="Loading changes" />
      ) : listQuery.isError ? (
        <LoadFailure message="Could not load the configuration history." onRetry={() => void listQuery.refetch()} retrying={listQuery.isFetching} />
      ) : changes.length === 0 ? (
        <div className="bg-white border border-marine/15 p-6 text-sm text-ink-mid">{narrowed ? 'No change matches.' : 'No changes on record yet. They appear here from the next change anyone makes.'}</div>
      ) : (
        <div className="space-y-4">
          {byDay.map(([day, dayChanges]) => (
            <section key={day}>
              <h3 className="mb-1 text-[11px] font-bold uppercase tracking-wide text-ink-mid">
                {new Date(`${day}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </h3>
              <ul className="bg-white border border-marine/15">
                {dayChanges.map((change) => (
                  <ConfigChangeRow
                    key={change.changeId}
                    change={change}
                    onSubject={(each) => update({ subject: { key: each.subjectKey, label: each.subjectLabel ?? each.subjectId } })}
                    onActor={(each) => update({ actor: { key: each.actorKey, label: actorName(each) } })}
                    onClient={(each) => (each.clientId ? update({ client: { id: each.clientId, label: each.organizationName ?? each.clientId } }) : undefined)}
                  />
                ))}
              </ul>
            </section>
          ))}
          {listQuery.hasNextPage && (
            <div className="flex justify-center">
              <Button icon={ChevronDown} variant="secondary" onClick={() => void listQuery.fetchNextPage()} disabled={listQuery.isFetchingNextPage}>
                {listQuery.isFetchingNextPage ? 'Loading…' : 'Older changes'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
